import _isEqual from 'lodash/isEqual';
import { restartableTask } from 'ember-concurrency';
import { IWidgetArgs } from 'volta/components/widgets';
import CollectionView, { CollectionViewResourceName } from 'volta/models/collection-view';
import {
  POResourceName,
  POValidationStatusesArray,
  StatusesArray
} from 'volta/models/planning-order';
import { POGroupResourceName } from 'volta/models/po-group';
import {
  ICollectionFilter,
  ICreateCollectionViewCmd,
  IPOWorkbenchCollectionArgs
} from 'volta/models/types/collection-view';
import {
  TDateField,
  TPOStatus,
  TPOStatusOption,
  TPOValidationStatus,
  TPOValidationStatusOption
} from 'volta/models/types/planning-order';
import POGroupsCollectionDefinitionBuilder, {
  DefaultSettings
} from 'volta/services/collection-builder-po-groups';
import SessionUserService from 'volta/services/session-user';
import StoreService from 'volta/services/store';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { collectionViewToQueryParams, serializeFilterValues } from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { THue, colorForCategory } from 'volta/utils/style-utils';
import { sum } from 'volta/utils/math-utils';
import { BufferZonesIndex, TBufferZone } from 'volta/models/constants/buffer-zones';
import _merge from 'lodash/merge';

export interface IPoWidget {
  poCount: number;
  unreadComments: number;
  schedulingAlerts: number;
  syncAlerts: number;
  reorderQtyValue: number;
  capacityAlerts: number;
  status: Partial<Record<TPOStatus, number>>;
  validationStatus: Record<TPOValidationStatus, number>;
  planningBuffer: Record<TBufferZone, number>;
  executionBuffer: Record<TBufferZone, number>;
  activities: Array<[number, number]>;
}
type TPOWidgetCollectionView =
  | CollectionView<IPOWorkbenchCollectionArgs>
  | (ICreateCollectionViewCmd<IPOWorkbenchCollectionArgs> & { id?: string });

interface IPlanningOrdersWidgetArgs {
  collectionViewId?: string;
  stats: { disabled: boolean };
  planningBuffer: { disabled: boolean };
  executionBuffer: { disabled: boolean };
  status: { disabled: boolean };
}

interface IPOCollectionViewAPIArgs {
  dateField: TDateField;
  showCapacity: boolean;
  filter: TQueryParams;
}
export default class PlanningOrdersWidget extends Component<
  IWidgetArgs<IPlanningOrdersWidgetArgs>
> {
  @service store!: StoreService;
  @service router!: RouterService;
  @service sessionUser!: SessionUserService;
  @service collectionBuilderPoGroups!: POGroupsCollectionDefinitionBuilder;

  @tracked poWidget?: IPoWidget;
  @tracked collectionView?: TPOWidgetCollectionView;

  lastQuery?: IPOCollectionViewAPIArgs;
  resourceName = POGroupResourceName;
  collectionDefinition = this.collectionBuilderPoGroups.build(this.args.queryParamsValues);

  get authorisedQueryParams() {
    const { queryParamsValues } = this.args;
    const authorisedQueryParams: TQueryParams = {};
    this.collectionDefinition.properties.forEach((p) => {
      if (queryParamsValues[p.property]) {
        authorisedQueryParams[p.property] = queryParamsValues[p.property];
      }
    });
    return authorisedQueryParams;
  }

  get cvQueryFiltersParams() {
    const filters = this.collectionView?.filters ?? [];
    return serializeFilterValues(
      filters.reduce((acc: TFilterValues, filter) => {
        const { property, values, comparator } = filter;
        acc[property] = {
          values,
          comparator,
          enabled: true
        };
        return acc;
      }, {}),
      this.collectionDefinition.filters
    );
  }

  get statusOptions() {
    const { status } = this.poWidget ?? {};
    return status
      ? StatusesArray.reduce((acc: TPOStatusOption[], opt) => {
          if (status[opt.key]) {
            acc.push({ ...opt, count: status[opt.key] });
          }
          return acc;
        }, [])
      : [];
  }

  get validationStatusOptions() {
    const { validationStatus } = this.poWidget ?? {};
    return validationStatus
      ? POValidationStatusesArray.reduce((acc: TPOValidationStatusOption[], opt) => {
          if (validationStatus[opt.key]) {
            acc.push({
              ...opt,
              count: validationStatus[opt.key],
              label: this.sessionUser.isSupplierUser ? opt.supplierLabel : opt.customerLabel
            });
          }
          return acc;
        }, [])
      : [];
  }

  /**
   * @returns {Object} - Planning and execution buffer zones percentage
   */
  @cached
  get bufferZones() {
    const { planningBuffer, executionBuffer } = { ...(this.poWidget ?? {}) };
    const planning: Partial<Record<TBufferZone, number>> = {};
    const execution: Partial<Record<TBufferZone, number>> = {};
    const total = sum(Object.values(planningBuffer ?? {}));

    if (planningBuffer && executionBuffer && total) {
      Object.entries(planningBuffer ?? {}).forEach(
        ([key, countPlanning]: [TBufferZone, number]) => {
          planning[key] = countPlanning / total;
          execution[key] = executionBuffer[key] / total;
        }
      );
    }

    return {
      planning,
      execution
    };
  }

  /**
   * @returns {Object} - Planning and execution buffer options
   */
  @cached
  get bufferOptions(): {
    planning: IStatusOption<TBufferZone>[];
    execution: IStatusOption<TBufferZone>[];
  } {
    const { planningBuffer, executionBuffer } = this.poWidget ?? {};
    const planning: IStatusOption<TBufferZone>[] = [];
    const execution: IStatusOption<TBufferZone>[] = [];

    if (planningBuffer) {
      Object.entries(planningBuffer).forEach(
        ([key, count]: [TBufferZone, number], index: number) => {
          const option = {
            index,
            key,
            value: key,
            label: key,
            count,
            color: colorForCategory(key) as THue
          };
          planning.push(option);
          execution.push({ ...option, count: executionBuffer?.[key] ?? 0 });
        }
      );
    }

    return {
      planning,
      execution
    };
  }

  get hasBufferDisabled() {
    const {
      widget: { args }
    } = this.args;
    return args?.planningBuffer.disabled || args?.executionBuffer.disabled;
  }

  /**
   * @returns {boolean} - True if there is only one status to display
   */
  get hasOneStatusOnly() {
    const s = this.statusOptions.length;
    const v = this.validationStatusOptions.length;
    return (!s && v) || (s && !v);
  }

  @action
  getCVQueryParams(filters: ICollectionFilter[]) {
    if (!this.collectionView) {
      return;
    }
    return collectionViewToQueryParams(
      {
        id: this.collectionView.id,
        filters: filters ?? [...(this.collectionView.filters ?? [])],
        sortBy: this.collectionView.sortBy,
        groupBy: this.collectionView.groupBy,
        pageSize: this.collectionView.pageSize,
        args: this.collectionView.args
      },
      this.authorisedQueryParams,
      this.collectionDefinition
    );
  }

  @action
  goToViewWithFilteredBuffer(bufferType: 'planningBuffer' | 'executionBuffer', zone: TBufferZone) {
    if (!this.collectionView) {
      return;
    }
    const withoutBuffer = [...(this.collectionView.filters ?? [])].filter(
      (f) => f.property !== bufferType
    );
    const value = `${BufferZonesIndex[zone]}`;

    return this.getCVQueryParams([
      ...withoutBuffer,
      {
        values: [value],
        property: bufferType,
        comparator: 'in'
      }
    ]);
  }

  @action
  goToViewWithFilteredStatus(status: TPOStatus) {
    if (!this.collectionView) {
      return;
    }
    const withoutStatus = [...(this.collectionView.filters ?? [])].filter(
      (f) => f.property !== 'status'
    );

    return this.getCVQueryParams([
      ...withoutStatus,
      {
        values: [status],
        property: 'status',
        comparator: 'in'
      }
    ]);
  }

  @action
  goToViewWithFilteredValidationStatus(validationStatus: TPOValidationStatus) {
    if (!this.collectionView) {
      return;
    }
    const withoutValidationStatus = [...(this.collectionView.filters ?? [])].filter(
      (f) => f.property !== 'validationStatus'
    );

    return this.getCVQueryParams([
      ...withoutValidationStatus,
      {
        values: [validationStatus],
        property: 'validationStatus',
        comparator: 'in'
      }
    ]);
  }

  fetchCollectionView = restartableTask(async () => {
    const { args } = this.args.widget;
    if (args?.collectionViewId) {
      this.collectionView = await this.store.findRecord(
        CollectionViewResourceName.singular,
        args?.collectionViewId
      );
    } else {
      this.collectionView = this.collectionDefinition.defaultView;
    }
  });

  fetchWidget = restartableTask(async () => {
    try {
      await this.fetchCollectionView.perform();
    } catch (e) {
      defaultErrorHandler(e);
      return;
    }

    const query = {
      dateField: this.collectionView!.args?.dateField ?? DefaultSettings.dateField,
      showCapacity: this.collectionView!.args?.showCapacity ?? DefaultSettings.showCapacity,
      filter: _merge(this.cvQueryFiltersParams, this.authorisedQueryParams)
    };

    if (!_isEqual(this.lastQuery, query)) {
      this.poWidget = await this.store.customQuery(
        POResourceName.plural,
        'widget',
        undefined,
        query
      );
      this.lastQuery = query;
    }
  });
}
